/**
 * Anonymous play statistics (ADR 0015): the port the game reports through and the wire format its one receiver — the
 * Cloudflare Pages Function `deploy/cloudflare/functions/api/e.ts`, which imports this file — accepts. No gameplay
 * reads anything back; the local save stays the only save (ADR 0012).
 *
 * Analytics Engine stores positional columns, so an event is a name, one short label and a fixed list of numbers:
 * the order of `ANALYTICS_NUMBERS` is the column order (double1 … doubleN) the queries in `tools/analytics/` use.
 * Append only — never reorder or reuse a slot, or older rows read wrong.
 */
export const ANALYTICS_EVENTS = ["session", "battle", "error"] as const;
export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[number];

export const ANALYTICS_NUMBERS = ["level", "won", "stage", "stages", "seconds", "stars", "highest", "sessions", "days", "installed"] as const;
export type AnalyticsNumber = (typeof ANALYTICS_NUMBERS)[number];

/** One thing that happened: `label` is the battle's mode, a session's campaign tag or an error's first words. */
export interface AnalyticsEvent {
  readonly name: AnalyticsEventName;
  readonly label?: string;
  readonly numbers?: Readonly<Partial<Record<AnalyticsNumber, number>>>;
}

/** Where the game reports. Fire and forget: never throws, never waits, and the game must not depend on it. */
export interface Analytics {
  track(event: AnalyticsEvent): void;
}

/** Local builds, the iOS app and tests: nothing is sent. */
export const NO_ANALYTICS: Analytics = { track: () => undefined };

export const ANALYTICS_WIRE_VERSION = 1;
export const ANALYTICS_LABEL_MAX = 96;
/** Events per request; the client flushes well before this. */
export const ANALYTICS_BATCH_MAX = 25;

/** One event on the wire: name, label, the numbers in `ANALYTICS_NUMBERS` order. */
export interface WireEvent { readonly n: AnalyticsEventName; readonly l: string; readonly x: readonly number[] }
/** A request body: the anonymous install id, the build channel and id, and up to `ANALYTICS_BATCH_MAX` events. */
export interface WireBatch { readonly v: number; readonly id: string; readonly ch: string; readonly b: string; readonly e: readonly WireEvent[] }

/** An event as it goes on the wire (missing numbers are 0; labels are cut to `ANALYTICS_LABEL_MAX`). */
export function toWire(event: AnalyticsEvent): WireEvent {
  const nums = event.numbers ?? {};
  return { n: event.name, l: (event.label ?? "").slice(0, ANALYTICS_LABEL_MAX), x: ANALYTICS_NUMBERS.map((k) => finite(nums[k])) };
}

const IdPattern = /^[0-9a-f-]{8,40}$/i;
const ShortText = /^[\w.:-]{0,40}$/;

/** Validates a request body from the outside world; null when anything is unexpected (the receiver drops it). */
export function parseWireBatch(raw: unknown): WireBatch | null {
  if (!isRecord(raw) || raw.v !== ANALYTICS_WIRE_VERSION) return null;
  const { id, ch, b, e } = raw;
  if (typeof id !== "string" || !IdPattern.test(id)) return null;
  if (typeof ch !== "string" || !ShortText.test(ch) || typeof b !== "string" || !ShortText.test(b)) return null;
  if (!Array.isArray(e) || e.length === 0 || e.length > ANALYTICS_BATCH_MAX) return null;
  const events: WireEvent[] = [];
  for (const item of e) {
    if (!isRecord(item)) return null;
    const { n, l, x } = item;
    if (typeof n !== "string" || !(ANALYTICS_EVENTS as readonly string[]).includes(n)) return null;
    if (typeof l !== "string" || l.length > ANALYTICS_LABEL_MAX) return null;
    if (!Array.isArray(x) || x.length > ANALYTICS_NUMBERS.length || !x.every((v) => typeof v === "number" && Number.isFinite(v))) return null;
    events.push({ n: n as AnalyticsEventName, l, x: ANALYTICS_NUMBERS.map((_, i) => finite(x[i])) });
  }
  return { v: ANALYTICS_WIRE_VERSION, id, ch, b, e: events };
}

function finite(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
